import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ScenarioRunPreflightCheck } from '../../api/api';

// The four outcomes the runner writes. A status it adds later is drawn like one it could not check, rather than
// as a pass it is not.
const LOOK: Record<string, { icon: IconProp; label: string }> = {
    passed: { icon: 'circle-check', label: 'passed' },
    failed: { icon: 'circle-xmark', label: 'failed' },
    warning: { icon: 'triangle-exclamation', label: 'could not be checked' },
    skipped: { icon: 'minus', label: 'not needed' },
};

const lookOf = (status?: string) => LOOK[status ?? ''] ?? LOOK.warning;

export const preflightKind = (status?: string) =>
    status && LOOK[status] ? status : 'warning';

/** Whether any check needs a look: one that failed, or one that could not be made. */
export const preflightNeedsAttention = (checks: ScenarioRunPreflightCheck[]) =>
    checks.some((check) =>
        ['failed', 'warning'].includes(preflightKind(check.status))
    );

/**
 * The scenarios a failed check stops, each with the checks that stop it.
 *
 * The picker marks those scenarios, so what a run would skip is visible before anybody starts one.
 */
export const blockedScenarios = (checks: ScenarioRunPreflightCheck[]) => {
    const blocked = new Map<string, string[]>();

    checks
        .filter((check) => preflightKind(check.status) === 'failed')
        .forEach((check) =>
            (check.blocks ?? []).forEach((scenario) =>
                blocked.set(scenario, [
                    ...(blocked.get(scenario) ?? []),
                    check.name ?? check.id ?? 'a check',
                ])
            )
        );

    return blocked;
};

/** The outcomes as counts with their icon, for a header that stays readable while the list is folded. */
export const PreflightCounts = ({
    checks,
}: {
    checks: ScenarioRunPreflightCheck[];
}) => (
    <>
        {['passed', 'failed', 'warning', 'skipped']
            .map((status) => ({
                status,
                count: checks.filter(
                    (check) => preflightKind(check.status) === status
                ).length,
            }))
            .filter((entry) => entry.count > 0)
            .map((entry) => (
                <span
                    key={entry.status}
                    className={`arl-preflight__count arl-preflight__count--${entry.status}`}
                >
                    <FontAwesomeIcon icon={lookOf(entry.status).icon} />
                    {entry.count} {lookOf(entry.status).label}
                </span>
            ))}
    </>
);

/** Every check with what it found, and which scenarios a failed one stops. */
export const PreflightChecks = ({
    checks,
}: {
    checks: ScenarioRunPreflightCheck[];
}) => (
    <ul className="arl-preflight__list">
        {checks.map((check) => {
            const kind = preflightKind(check.status);

            return (
                <li
                    key={check.id}
                    className={`arl-preflight__check arl-preflight__check--${kind}`}
                >
                    <FontAwesomeIcon
                        icon={lookOf(kind).icon}
                        className="arl-preflight__icon"
                        title={lookOf(kind).label}
                    />
                    <div className="arl-preflight__body">
                        <div className="arl-preflight__head">
                            <span className="arl-preflight__system">
                                {check.system}
                            </span>
                            <strong>{check.name}</strong>
                        </div>
                        {check.actual && (
                            <div className="arl-preflight__actual">
                                {check.actual}
                                {check.expected && kind === 'failed' && (
                                    <span className="arl-preflight__expected">
                                        {' '}
                                        &middot; needed: {check.expected}
                                    </span>
                                )}
                            </div>
                        )}
                        {check.detail && (
                            <div className="arl-preflight__detail">
                                {check.detail}
                            </div>
                        )}
                        {(check.blocks ?? []).length > 0 && (
                            <div className="arl-preflight__blocks">
                                Not run:{' '}
                                {(check.blocks ?? []).map((scenario, index) => (
                                    <span key={scenario}>
                                        {index > 0 && ', '}
                                        <code>{scenario}</code>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </li>
            );
        })}
    </ul>
);

/**
 * What the run checked about the environment before its first scenario.
 *
 * Open by default only when something did not pass: a list of green checks is worth one line, a failed one is the
 * reason scenarios were not run and has to be readable without a click.
 */
export const ScenarioPreflight = ({
    checks,
}: {
    checks: ScenarioRunPreflightCheck[];
}) => (
    <details className="arl-preflight" open={preflightNeedsAttention(checks)}>
        <summary className="arl-preflight__summary">
            <h3 className="arl-preflight__title">Pre-flight checks</h3>
            <PreflightCounts checks={checks} />
        </summary>

        <PreflightChecks checks={checks} />
    </details>
);
